import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Car, User, ArrowRight, ArrowLeft, CheckCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RegistrationForm = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPackage = searchParams.get('package');
  
  const [step, setStep] = useState(initialPackage ? 1 : 0); // Step 0 = package selection
  const [selectedPackage, setSelectedPackage] = useState(initialPackage || '');
  const [loading, setLoading] = useState(false);
  
  const [clientData, setClientData] = useState({
    civilite: "",
    nom: "",
    prenom: "",
    date_naissance: "",
    lieu_naissance: "",
    adresse: "",
    code_postal: "",
    ville: "",
    email: "",
    telephone: "",
    password: "",
    confirmPassword: ""
  });
  
  const [vehicleData, setVehicleData] = useState({
    marque: "",
    modele: "",
    type_vehicule: "",
    numero_serie: "",
    date_premiere_immat: "",
    date_achat: "",
    couleur: "",
    pays_origine: "",
    energie: "",
    co2: ""
  });

  const selectPackage = (pkg) => {
    setSelectedPackage(pkg);
    setSearchParams({ package: pkg });
    setStep(1);
    window.scrollTo(0, 0);
  };

  const handleClientChange = (field, value) => {
    setClientData(prev => ({ ...prev, [field]: value }));
  };

  const handleVehicleChange = (field, value) => {
    setVehicleData(prev => ({ ...prev, [field]: value }));
  };

  const validateStep1 = () => {
    const required = ['civilite', 'nom', 'prenom', 'date_naissance', 'adresse', 'code_postal', 'ville', 'email', 'telephone', 'password'];
    for (const field of required) {
      if (!clientData[field]) {
        const fieldNames = {
          civilite: 'Civilité',
          nom: 'Nom',
          prenom: 'Prénom',
          date_naissance: 'Date de naissance',
          adresse: 'Adresse',
          code_postal: 'Code postal',
          ville: 'Ville',
          email: 'Email',
          telephone: 'Téléphone',
          password: 'Mot de passe'
        };
        toast.error(`Le champ "${fieldNames[field] || field}" est requis`);
        return false;
      }
    }
    if (!clientData.email.includes('@')) {
      toast.error("Veuillez entrer un email valide");
      return false;
    }
    if (clientData.password.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return false;
    }
    if (clientData.password !== clientData.confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    const required = ['marque', 'modele', 'numero_serie', 'date_premiere_immat', 'date_achat', 'couleur', 'pays_origine', 'energie'];
    for (const field of required) {
      if (!vehicleData[field]) {
        toast.error(`Le champ "${field}" est requis`);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateStep2()) return;
    
    setLoading(true);
    try {
      // First, try to create the client account
      try {
        await axios.post(`${API}/client/register`, {
          email: clientData.email,
          password: clientData.password,
          nom: clientData.nom,
          prenom: clientData.prenom,
          telephone: clientData.telephone
        });
        // Store token for auto-login after dossier creation
        const loginResponse = await axios.post(`${API}/client/login`, {
          email: clientData.email,
          password: clientData.password
        });
        localStorage.setItem('client_token', loginResponse.data.token);
        localStorage.setItem('client_data', JSON.stringify(loginResponse.data.client));
      } catch (registerError) {
        // If account already exists, that's okay - continue with dossier creation
        if (registerError.response?.status !== 400 || !registerError.response?.data?.detail?.includes('existe')) {
          console.log("Note: Could not create account, may already exist");
        }
      }

      // Create the dossier (exclude password fields from client data)
      const { password, confirmPassword, ...clientDataWithoutPassword } = clientData;
      const response = await axios.post(`${API}/dossiers`, {
        client: clientDataWithoutPassword,
        vehicle: vehicleData,
        package_type: selectedPackage || 'standard'
      });
      
      toast.success("Dossier créé avec succès ! Votre compte client a été créé.");
      navigate(`/signature/${response.data.id}`);
    } catch (error) {
      toast.error("Erreur lors de la création du dossier");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
      window.scrollTo(0, 0);
    } else if (step === 2) {
      handleSubmit();
    }
  };

  const prevStep = () => {
    if (step === 1) {
      setStep(0); // Go back to package selection
      window.scrollTo(0, 0);
    } else if (step > 1) {
      setStep(step - 1);
      window.scrollTo(0, 0);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#000091] rounded-lg flex items-center justify-center">
              <Car className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-[#000091]">Carte Grise Étrangère</h1>
              <p className="text-xs text-slate-500">Demande d'immatriculation</p>
            </div>
          </div>
        </div>
        <div className="h-1 flex">
          <div className="flex-1 bg-[#002395]"></div>
          <div className="flex-1 bg-white"></div>
          <div className="flex-1 bg-[#ED2939]"></div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= 1 ? 'bg-[#000091] text-white' : 'bg-slate-200 text-slate-500'}`}>
                {step > 1 ? <CheckCircle className="w-5 h-5" /> : '1'}
              </div>
              <span className={`text-sm font-medium ${step >= 1 ? 'text-[#000091]' : 'text-slate-500'}`}>Informations personnelles</span>
            </div>
            <div className="flex-1 h-0.5 bg-slate-200 mx-4">
              <div className={`h-full bg-[#000091] transition-all ${step > 1 ? 'w-full' : 'w-0'}`}></div>
            </div>
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= 2 ? 'bg-[#000091] text-white' : 'bg-slate-200 text-slate-500'}`}>
                2
              </div>
              <span className={`text-sm font-medium ${step >= 2 ? 'text-[#000091]' : 'text-slate-500'}`}>Informations véhicule</span>
            </div>
          </div>
          <Progress value={step === 0 ? 0 : step === 1 ? 50 : 100} className="h-2" />
        </div>

        {/* Step 0: Package Selection */}
        {step === 0 && (
          <div className="space-y-6">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#0F172A]">
                  <Car className="w-5 h-5 text-[#000091]" />
                  Choisissez votre formule
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 mb-6">
                  Sélectionnez la formule adaptée à votre situation :
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Standard Package */}
                  <Card 
                    className={`cursor-pointer transition-all hover:shadow-lg border-2 ${selectedPackage === 'standard' ? 'border-[#000091] bg-[#000091]/5' : 'border-slate-200 hover:border-[#000091]/50'}`}
                    onClick={() => selectPackage('standard')}
                    data-testid="package-standard"
                  >
                    <CardContent className="p-6">
                      <div className="text-center">
                        <h3 className="text-xl font-bold text-[#0F172A] mb-2">Immatriculation Définitive</h3>
                        <div className="text-3xl font-bold text-[#000091] mb-4">99€</div>
                        <ul className="text-sm text-slate-600 space-y-2 text-left">
                          <li className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            Carte grise française définitive
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            Génération des CERFA
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            Suivi de dossier en ligne
                          </li>
                        </ul>
                        <p className="text-xs text-slate-500 mt-4">Idéal si votre véhicule est déjà en France</p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Premium Package */}
                  <Card 
                    className={`cursor-pointer transition-all hover:shadow-lg border-2 relative ${selectedPackage === 'premium' ? 'border-[#000091] bg-[#000091]/5' : 'border-slate-200 hover:border-[#000091]/50'}`}
                    onClick={() => selectPackage('premium')}
                    data-testid="package-premium"
                  >
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-[#ED2939] text-white text-xs font-semibold px-3 py-1 rounded-full">
                        RECOMMANDÉ
                      </span>
                    </div>
                    <CardContent className="p-6 pt-8">
                      <div className="text-center">
                        <h3 className="text-xl font-bold text-[#0F172A] mb-2">WW Provisoire + Définitive</h3>
                        <div className="text-3xl font-bold text-[#000091] mb-4">149€</div>
                        <ul className="text-sm text-slate-600 space-y-2 text-left">
                          <li className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            Plaque WW provisoire immédiate
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            Puis carte grise définitive
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            Circulez légalement dès réception
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            Suivi de dossier en ligne
                          </li>
                        </ul>
                        <p className="text-xs text-slate-500 mt-4">Idéal pour conduire rapidement votre véhicule</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Package info - only show when step > 0 */}
        {step > 0 && (
          <div className="bg-[#000091]/5 border border-[#000091]/20 rounded-lg p-4 mb-8">
            <p className="text-sm text-[#000091]">
              <span className="font-semibold">Formule sélectionnée:</span>{' '}
              {selectedPackage === 'premium' ? 'WW Provisoire + Définitive (149€)' : 'Immatriculation Définitive (99€)'}
            </p>
          </div>
        )}

        {/* Step 1: Client Info */}
        {step === 1 && (
          <Card className="border-slate-200" data-testid="client-info-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#0F172A]">
                <User className="w-5 h-5 text-[#000091]" />
                Informations personnelles
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Civilité */}
              <div className="space-y-2">
                <Label>Civilité *</Label>
                <Select 
                  value={clientData.civilite} 
                  onValueChange={(value) => handleClientChange('civilite', value)}
                >
                  <SelectTrigger data-testid="civilite-select">
                    <SelectValue placeholder="Sélectionnez" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="M.">Monsieur</SelectItem>
                    <SelectItem value="Mme">Madame</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Nom & Prénom */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nom *</Label>
                  <Input 
                    value={clientData.nom}
                    onChange={(e) => handleClientChange('nom', e.target.value)}
                    placeholder="DUPONT"
                    data-testid="nom-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Prénom *</Label>
                  <Input 
                    value={clientData.prenom}
                    onChange={(e) => handleClientChange('prenom', e.target.value)}
                    placeholder="Jean"
                    data-testid="prenom-input"
                  />
                </div>
              </div>

              {/* Date & Lieu de naissance */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date de naissance *</Label>
                  <Input 
                    type="date"
                    value={clientData.date_naissance}
                    onChange={(e) => handleClientChange('date_naissance', e.target.value)}
                    data-testid="date-naissance-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Lieu de naissance</Label>
                  <Input 
                    value={clientData.lieu_naissance}
                    onChange={(e) => handleClientChange('lieu_naissance', e.target.value)}
                    placeholder="Paris"
                    data-testid="lieu-naissance-input"
                  />
                </div>
              </div>

              {/* Adresse */}
              <div className="space-y-2">
                <Label>Adresse *</Label>
                <Input 
                  value={clientData.adresse}
                  onChange={(e) => handleClientChange('adresse', e.target.value)}
                  placeholder="123 Rue de la République"
                  data-testid="adresse-input"
                />
              </div>

              {/* Code postal & Ville */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Code postal *</Label>
                  <Input 
                    value={clientData.code_postal}
                    onChange={(e) => handleClientChange('code_postal', e.target.value)}
                    placeholder="75001"
                    data-testid="code-postal-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ville *</Label>
                  <Input 
                    value={clientData.ville}
                    onChange={(e) => handleClientChange('ville', e.target.value)}
                    placeholder="Paris"
                    data-testid="ville-input"
                  />
                </div>
              </div>

              {/* Contact */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input 
                    type="email"
                    value={clientData.email}
                    onChange={(e) => handleClientChange('email', e.target.value)}
                    placeholder="jean.dupont@email.com"
                    data-testid="email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Téléphone *</Label>
                  <Input 
                    type="tel"
                    value={clientData.telephone}
                    onChange={(e) => handleClientChange('telephone', e.target.value)}
                    placeholder="06 12 34 56 78"
                    data-testid="telephone-input"
                  />
                </div>
              </div>

              {/* Création de compte - Mot de passe */}
              <div className="border-t border-slate-200 pt-6 mt-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-blue-800">
                    <strong>Créez votre compte :</strong> Votre mot de passe vous permettra d'accéder à votre espace client pour suivre l'avancement de votre dossier.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Mot de passe *</Label>
                    <Input 
                      type="password"
                      value={clientData.password}
                      onChange={(e) => handleClientChange('password', e.target.value)}
                      placeholder="Minimum 6 caractères"
                      data-testid="password-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Confirmez le mot de passe *</Label>
                    <Input 
                      type="password"
                      value={clientData.confirmPassword}
                      onChange={(e) => handleClientChange('confirmPassword', e.target.value)}
                      placeholder="Confirmez votre mot de passe"
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Vehicle Info */}
        {step === 2 && (
          <Card className="border-slate-200" data-testid="vehicle-info-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#0F172A]">
                <Car className="w-5 h-5 text-[#000091]" />
                Informations du véhicule
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Marque & Modèle */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Marque *</Label>
                  <Input 
                    value={vehicleData.marque}
                    onChange={(e) => handleVehicleChange('marque', e.target.value)}
                    placeholder="BMW"
                    data-testid="marque-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Modèle *</Label>
                  <Input 
                    value={vehicleData.modele}
                    onChange={(e) => handleVehicleChange('modele', e.target.value)}
                    placeholder="Série 3"
                    data-testid="modele-input"
                  />
                </div>
              </div>

              {/* Type & VIN */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type de véhicule</Label>
                  <Select 
                    value={vehicleData.type_vehicule} 
                    onValueChange={(value) => handleVehicleChange('type_vehicule', value)}
                  >
                    <SelectTrigger data-testid="type-vehicule-select">
                      <SelectValue placeholder="Sélectionnez" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="VP">Véhicule particulier (VP)</SelectItem>
                      <SelectItem value="CTTE">Camionnette (CTTE)</SelectItem>
                      <SelectItem value="MTL">Moto (MTL)</SelectItem>
                      <SelectItem value="CL">Caravane (CL)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Numéro de série (VIN) *</Label>
                  <Input 
                    value={vehicleData.numero_serie}
                    onChange={(e) => handleVehicleChange('numero_serie', e.target.value.toUpperCase())}
                    placeholder="WBAXXXXXXX"
                    maxLength={17}
                    data-testid="vin-input"
                  />
                </div>
              </div>

              {/* Date 1ère immat & Date d'achat */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date de 1ère mise en circulation *</Label>
                  <Input 
                    type="date"
                    value={vehicleData.date_premiere_immat}
                    onChange={(e) => handleVehicleChange('date_premiere_immat', e.target.value)}
                    data-testid="date-immat-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date d'achat du véhicule *</Label>
                  <Input 
                    type="date"
                    value={vehicleData.date_achat}
                    onChange={(e) => handleVehicleChange('date_achat', e.target.value)}
                    data-testid="date-achat-input"
                  />
                </div>
              </div>

              {/* Couleur & Pays */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Couleur dominante du véhicule *</Label>
                  <Select 
                    value={vehicleData.couleur} 
                    onValueChange={(value) => handleVehicleChange('couleur', value)}
                  >
                    <SelectTrigger data-testid="couleur-select">
                      <SelectValue placeholder="Sélectionnez la couleur" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NOIR">Noir</SelectItem>
                      <SelectItem value="BLANC">Blanc</SelectItem>
                      <SelectItem value="GRIS">Gris</SelectItem>
                      <SelectItem value="BLEU">Bleu</SelectItem>
                      <SelectItem value="ROUGE">Rouge</SelectItem>
                      <SelectItem value="VERT">Vert</SelectItem>
                      <SelectItem value="JAUNE">Jaune</SelectItem>
                      <SelectItem value="ORANGE">Orange</SelectItem>
                      <SelectItem value="MARRON">Marron</SelectItem>
                      <SelectItem value="BEIGE">Beige</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Pays d'origine *</Label>
                  <Select 
                    value={vehicleData.pays_origine} 
                    onValueChange={(value) => handleVehicleChange('pays_origine', value)}
                  >
                    <SelectTrigger data-testid="pays-origine-select">
                      <SelectValue placeholder="Sélectionnez le pays" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Allemagne">🇩🇪 Allemagne</SelectItem>
                      <SelectItem value="Belgique">🇧🇪 Belgique</SelectItem>
                      <SelectItem value="Pays-Bas">🇳🇱 Pays-Bas</SelectItem>
                      <SelectItem value="Espagne">🇪🇸 Espagne</SelectItem>
                      <SelectItem value="Autriche">🇦🇹 Autriche</SelectItem>
                      <SelectItem value="Italie">🇮🇹 Italie</SelectItem>
                      <SelectItem value="Suède">🇸🇪 Suède</SelectItem>
                      <SelectItem value="Portugal">🇵🇹 Portugal</SelectItem>
                      <SelectItem value="Autre UE">🇪🇺 Autre pays de l'Union Européenne</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-amber-600 mt-1">
                    ⚠️ Véhicules hors UE : contactez-nous pour une étude personnalisée
                  </p>
                </div>
              </div>

              {/* Energie & CO2 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Énergie / Carburant *</Label>
                  <Select 
                    value={vehicleData.energie} 
                    onValueChange={(value) => handleVehicleChange('energie', value)}
                  >
                    <SelectTrigger data-testid="energie-select">
                      <SelectValue placeholder="Sélectionnez" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Essence">Essence</SelectItem>
                      <SelectItem value="Diesel">Diesel</SelectItem>
                      <SelectItem value="Électrique">Électrique</SelectItem>
                      <SelectItem value="Hybride">Hybride</SelectItem>
                      <SelectItem value="Hybride rechargeable">Hybride rechargeable</SelectItem>
                      <SelectItem value="GPL">GPL</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Émissions CO2 (g/km)</Label>
                  <Input 
                    type="number"
                    value={vehicleData.co2}
                    onChange={(e) => handleVehicleChange('co2', e.target.value)}
                    placeholder="Optionnel - visible sur la carte grise"
                    data-testid="co2-input"
                  />
                  <p className="text-xs text-slate-500">Si vous ne connaissez pas cette valeur, laissez vide</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Navigation buttons */}
        <div className="flex justify-between mt-8">
          <Button
            variant="outline"
            onClick={step === 0 ? () => navigate('/') : step === 1 ? () => setStep(0) : prevStep}
            className="border-slate-300"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {step === 0 ? "Retour à l'accueil" : step === 1 ? "Changer de formule" : "Étape précédente"}
          </Button>
          
          {step === 0 ? (
            <div></div> // No next button at step 0, user clicks on package to proceed
          ) : (
            <Button
              onClick={nextStep}
              disabled={loading}
              className="bg-[#000091] hover:bg-[#000091]/90"
              data-testid="next-btn"
            >
              {loading ? "Création..." : step === 2 ? "Créer le dossier" : "Continuer"}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </main>
    </div>
  );
};

export default RegistrationForm;
